/**
 * #1226 — a Polymarket US market's name in the shape the Tracker's Kalshi and
 * international Polymarket rows use: `BAL @ BUF — NFL — 09-28`. The venue's
 * slug (`nfl-baltimore-at-buffalo-2026-09-28`) is evidence and lives in hover
 * titles; a cell carries, in order, the title the relay recorded for the
 * market, the matched board row's team codes, the codes the Kalshi team
 * registry knows for the slug's names, or the slug's names themselves
 * (`Mississippi @ Florida`). A slug this grammar does not read stays as it is,
 * never a guess.
 *
 * The venue's own slugs read `<family>-<sport>-<away>-<home>-<YYYY-MM-DD>[-<line>]`
 * (`aec` game, `asc` spread, `tsc` total; `aec-nba-mil-ind-2026-10-16`,
 * `asc-cfb-soumis-troy-2026-10-06-pos-28pt5`, `tsc-cfb-soumis-troy-2026-10-06-1h-9pt5`).
 * Their team codes are the K/P label already, so they win over a recorded
 * title: `MIL @ IND — NBA — 10-16`, with the line after the matchup.
 */
import { truncateTicker } from '@/lib/kalshiTicker/describeMarket';
import { MLB_TEAM_NAMES, NBA_TEAM_NAMES, NFL_TEAM_NAMES, NHL_TEAM_NAMES, WNBA_TEAM_NAMES } from '@/lib/kalshiTicker/teamNames';

export type UsSlugParts = {
  /** The slug's league prefix, upper-cased (`NFL`, `CFB`). */
  sport: string;
  /** Hyphenated names as the slug spells them (`baltimore`, `new-york-giants`). */
  away: string;
  home: string;
  /** `MM-DD`, the Tracker's date shape. */
  date: string;
};

export type UsMarketTarget = { marketSlug: string; marketTitle?: string | null };
export type UsMarketTeams = { away: string; home: string };

export type UsMarketLabel = {
  /** The matchup line (`BAL @ BUF`, `Ravens at Bills`), or the slug when nothing better is known. */
  title: string;
  sport: string | null;
  date: string | null;
  /** The one-line table label: title — sport — date (whatever is known). */
  label: string;
  /** The card subtitle's last segment, as a Kalshi card ends with its ticker
   * tail; null when the title already is the slug (never the same string twice). */
  tail: string | null;
};

const SLUG = /^([a-z0-9]+)-(.+?)-at-(.+)-(\d{4})-(\d{2})-(\d{2})$/;
const VENUE_SLUG = /^(aec|asc|tsc)-([a-z0-9]+)-([a-z0-9]+)-([a-z0-9]+)-(\d{4})-(\d{2})-(\d{2})(?:-([a-z0-9-]+))?$/;

export type UsVenueSlugParts = {
  family: 'aec' | 'asc' | 'tsc';
  sport: string;
  /** Upper-cased venue team codes (`MIL`, `SOUMIS`). */
  away: string;
  home: string;
  date: string;
  /** The line after the matchup (`MIL +28.5`, `1H O/U 9.5`, `SOUMIS TT O/U 6.5`), or null for a game market. */
  line: string | null;
};

const PERIOD = /^(?:[1-4][hqp]|f5)$/;
const number = (t: string) => (/^\d+(?:pt\d+)?$/.test(t) ? t.replace('pt', '.') : null);

/** The venue's own `family-sport-away-home-date[-line]` slug, or null. A line
 * this grammar does not read leaves the slug unparsed rather than guessed. */
export function parseUsVenueSlug(slug: string): UsVenueSlugParts | null {
  const m = VENUE_SLUG.exec(slug);
  if (!m) return null;
  const [, family, sport, away, home, , mm, dd, rest] = m;
  const base = { family: family as UsVenueSlugParts['family'], sport: sport.toUpperCase(), away: away.toUpperCase(),
    home: home.toUpperCase(), date: `${mm}-${dd}` };
  if (!rest) return family === 'aec' ? { ...base, line: null } : null;
  const tokens = rest.split('-');
  const words: string[] = [];
  if (tokens.length && PERIOD.test(tokens[0])) words.push(tokens.shift()!.toUpperCase());
  if (family === 'asc') {
    const [sign, value, ...extra] = tokens;
    const n = value ? number(value) : null;
    if (extra.length || !n || (sign !== 'pos' && sign !== 'neg')) return null;
    words.push(`${base.away} ${sign === 'pos' ? '+' : '-'}${n}`);
  } else if (family === 'tsc') {
    if (tokens[0] === 'tt' && tokens.length === 3 && number(tokens[2])) words.push(`${tokens[1].toUpperCase()} TT O/U ${number(tokens[2])}`);
    else if (tokens.length === 1 && number(tokens[0])) words.push(`O/U ${number(tokens[0])}`);
    else return null;
  } else return null;
  return { ...base, line: words.join(' ') };
}

/** `league-away-at-home-YYYY-MM-DD`, or null for any other shape. */
export function parseUsMarketSlug(slug: string): UsSlugParts | null {
  const m = SLUG.exec(slug);
  if (!m) return null;
  return { sport: m[1].toUpperCase(), away: m[2], home: m[3], date: `${m[5]}-${m[6]}` };
}

const REGISTRIES: Record<string, Record<string, string>> = {
  NFL: NFL_TEAM_NAMES, NBA: NBA_TEAM_NAMES, WNBA: WNBA_TEAM_NAMES, NHL: NHL_TEAM_NAMES, MLB: MLB_TEAM_NAMES,
};
const slugWord = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
/** Per sport: a slug-spelled name (full, city or nickname) to its code; null
 * where two teams share it (`new-york`, `los-angeles`). */
const reverse = new Map<string, Map<string, string | null>>();
function codesFor(sport: string): Map<string, string | null> | null {
  const names = REGISTRIES[sport];
  if (!names) return null;
  let index = reverse.get(sport);
  if (index) return index;
  index = new Map();
  const fullNameOf = new Map<string, string>();
  for (const [code, name] of Object.entries(names)) {
    const full = slugWord(name);
    const words = full.split('-');
    // Two codes for one team (`ARI`/`AZ`): the first listed stands.
    if (fullNameOf.has(full)) continue;
    fullNameOf.set(full, code);
    for (const key of new Set([full, words.slice(0, -1).join('-'), words[words.length - 1]])) {
      if (!key) continue;
      index.set(key, index.has(key) ? null : code);
    }
  }
  reverse.set(sport, index);
  return index;
}

/** The registry's code for a slug-spelled team name, or null (unknown sport, unknown or shared name). */
export function usTeamCode(sport: string, name: string): string | null {
  return codesFor(sport.toUpperCase())?.get(slugWord(name)) ?? null;
}

const titleCase = (hyphenated: string) => hyphenated.split('-').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

/** The matchup line alone: the recorded title, the board's codes, the registry's codes, the slug's names, the slug. */
export function usMarketTitle(target: UsMarketTarget, teams?: UsMarketTeams | null): string {
  const venue = parseUsVenueSlug(target.marketSlug);
  if (venue) return `${venue.away} @ ${venue.home}${venue.line ? ' ' + venue.line : ''}`;
  if (target.marketTitle) return target.marketTitle;
  if (teams) return `${teams.away} @ ${teams.home}`;
  const parts = parseUsMarketSlug(target.marketSlug);
  if (!parts) return target.marketSlug;
  const away = usTeamCode(parts.sport, parts.away);
  const home = usTeamCode(parts.sport, parts.home);
  return away && home ? `${away} @ ${home}` : `${titleCase(parts.away)} @ ${titleCase(parts.home)}`;
}

/**
 * The row's label parts. `sport` is the catalogue's answer when the host has
 * one (it wins over the slug's prefix); `teams` the matched board row's codes.
 */
export function usMarketLabel(target: UsMarketTarget, opts: { sport?: string | null; teams?: UsMarketTeams | null } = {}): UsMarketLabel {
  const venue = parseUsVenueSlug(target.marketSlug);
  const parts = venue ? null : parseUsMarketSlug(target.marketSlug);
  const title = usMarketTitle(target, opts.teams);
  const sport = opts.sport?.toUpperCase() ?? venue?.sport ?? parts?.sport ?? null;
  const date = venue?.date ?? parts?.date ?? null;
  const label = [title, sport, date].filter(Boolean).join(' — ');
  const tail = title === target.marketSlug ? null
    : venue ? `...${target.marketSlug.split('-').slice(2).join('-')}`
      : parts ? `...${parts.away}-at-${parts.home}` : truncateTicker(target.marketSlug);
  return { title, sport, date, label, tail };
}
